import { Button, Modal } from 'antd'
import {
  ADVICEPREP_BANDS,
  ADVICEPREP_ENTRY_CONNECTIONS,
  ADVICEPREP_ENTRY_FEE,
  advicePrepBandFor,
  advicePrepDailyCharge,
  advicePrepDailyRate,
  calculateAdvicePrepPrice,
  formatAdvicePrepCurrency,
} from '../pricing'

// Placeholder copy and examples until marketing supplies the final explanation.
const MONTH_EXAMPLES = [4, 10, 30]
const DAY_EXAMPLES = [
  { day: 1, connections: 25, change: '' },
  { day: 2, connections: 26, change: 'add 1 → ' },
]

type AdvicePrepBillingExplainerProps = {
  open: boolean
  onClose: () => void
}

export default function AdvicePrepBillingExplainer({ open, onClose }: AdvicePrepBillingExplainerProps) {
  const sampleRate = ADVICEPREP_BANDS[0]?.rate ?? 0

  return (
    <Modal
      open={open}
      title="How AdvicePrep billing works"
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          Got it
        </Button>
      }
    >
      <div className="space-y-5 text-sm leading-6 text-gray-700">
        <section>
          <h3 className="mb-1 font-display text-sm font-semibold text-brand-700">Your monthly plan</h3>
          <p className="m-0">
            AdvicePrep costs {formatAdvicePrepCurrency(ADVICEPREP_ENTRY_FEE)} a month, and that covers your first{' '}
            {ADVICEPREP_ENTRY_CONNECTIONS} client Xero connections.
          </p>
        </section>

        <section>
          <h3 className="mb-1 font-display text-sm font-semibold text-brand-700">
            Extra connections are counted every day
          </h3>
          <p className="m-0 mb-2">
            Each day we count your Xero connections. Every connection after your first {ADVICEPREP_ENTRY_CONNECTIONS}{' '}
            is charged for that day at one rate, set by your total that day. The more you connect, the lower the rate
            for all of them.
          </p>
          <table className="w-full border-collapse">
            <thead>
              <tr className="text-left text-xs text-gray-600">
                <th className="border-b border-gray-200 py-2 font-medium">Your total connections</th>
                <th className="border-b border-gray-200 py-2 text-right font-medium">Rate per extra connection</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border-b border-gray-100 py-2">1 – {ADVICEPREP_ENTRY_CONNECTIONS}</td>
                <td className="border-b border-gray-100 py-2 text-right">Included in plan</td>
              </tr>
              {ADVICEPREP_BANDS.map((band) => (
                <tr key={band.label}>
                  <td className="border-b border-gray-100 py-2">{band.label}</td>
                  <td className="border-b border-gray-100 py-2 text-right">
                    {formatAdvicePrepCurrency(band.rate)} a month
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="m-0 mt-2 text-xs text-gray-500">
            A day costs 1/365 of a year's rate. For example, {formatAdvicePrepCurrency(sampleRate)} a month is{' '}
            {formatAdvicePrepCurrency(sampleRate * 12)} a year, or about{' '}
            {formatAdvicePrepCurrency(advicePrepDailyRate(sampleRate), 2)} a day.
          </p>
        </section>

        <section>
          <h3 className="mb-2 font-display text-sm font-semibold text-brand-700">
            Examples for a month at the same count
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {MONTH_EXAMPLES.map((connections) => {
              const band = advicePrepBandFor(connections)
              const extra = connections - ADVICEPREP_ENTRY_CONNECTIONS
              return (
                <div key={connections} className="rounded-lg border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">{connections} connections</div>
                  <div className="mt-0.5 font-display text-xl font-bold text-gray-900">
                    {formatAdvicePrepCurrency(calculateAdvicePrepPrice(connections), 2)}
                  </div>
                  <div className="mt-1.5 text-xs leading-5 text-gray-600">
                    {band ? (
                      <>
                        <div>{formatAdvicePrepCurrency(ADVICEPREP_ENTRY_FEE)} plan</div>
                        <div>
                          + {extra} extra × {formatAdvicePrepCurrency(band.rate)} ={' '}
                          {formatAdvicePrepCurrency(extra * band.rate)}
                        </div>
                      </>
                    ) : (
                      'All covered by your plan'
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <p className="m-0 mt-2 text-xs text-gray-500">
            These are averages. Because usage is charged by the day, a 31-day month costs a little more and a 30-day
            month a little less.
          </p>
        </section>

        <section>
          <h3 className="mb-2 font-display text-sm font-semibold text-brand-700">When your count changes</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {DAY_EXAMPLES.map(({ day, connections, change }) => {
              const charge = advicePrepDailyCharge(connections)
              return (
                <div key={day} className="rounded-lg border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">
                    Day {day} · {change}
                    {connections} connections
                  </div>
                  <div className="mt-0.5 font-display text-xl font-bold text-gray-900">
                    <span className="font-sans text-xs font-medium text-gray-500">about</span>{' '}
                    {formatAdvicePrepCurrency(charge?.amount ?? 0, 2)}{' '}
                    <span className="font-sans text-xs font-medium text-gray-500">for the day</span>
                  </div>
                  {charge && (
                    <div className="mt-1.5 text-xs leading-5 text-gray-600">
                      {charge.extra} extra at the {formatAdvicePrepCurrency(charge.band.rate)} rate
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <p className="m-0 mt-2 text-xs text-gray-500">
            Reaching a new band moves every extra connection to the lower rate.
          </p>
        </section>

        <section>
          <h3 className="mb-1 font-display text-sm font-semibold text-brand-700">When you're charged</h3>
          <p className="m-0">
            On your billing date we charge your flat fee together with the extra connections counted during the
            period. Your estimate grows each day until then.
          </p>
          <p className="m-0 mt-2 text-xs text-gray-500">All prices exclude GST.</p>
        </section>
      </div>
    </Modal>
  )
}
